import { z } from 'zod';
import { MoneySchema } from './common.js';
// Wire shape for Decimal columns — Prisma emits canonical strings. Every money
// figure in a book-to-tax (ภ.ง.ด.50) reconciliation is signed: add-backs are
// positive but deducts, and the net book/tax/adjustment amounts, can be negative.
// So accept a string or any number (MoneySchema rejects negative strings → the
// negative lands on the z.coerce.number() branch).
const SignedDecimalLike = z.union([MoneySchema, z.coerce.number()]);
// ============================================================
// Enums (mirror Prisma)
// ============================================================
export const TaxAdjStatusSchema = z.enum(['DRAFT', 'APPROVED']);
export const TaxAdjCategorySchema = z.enum(['ADD_BACK', 'DEDUCT', 'TIMING', 'PERMANENT']);
// Sub-category codes from the prototype (16 entries). Open enum on the wire
// — the UI enforces the closed catalogue — so future RD circulars can add
// codes without a schema bump.
export const TaxAdjCodeSchema = z.string().min(1);
// ============================================================
// Row schema — read shape
// ============================================================
export const TaxAdjustmentSchema = z.object({
    id: z.uuid(),
    companyId: z.uuid(),
    taxYear: z.number().int().min(2000).max(2100),
    code: TaxAdjCodeSchema,
    category: TaxAdjCategorySchema,
    description: z.string(),
    section: z.string().nullable().optional(),
    bookAmount: SignedDecimalLike,
    taxAmount: SignedDecimalLike,
    adjustmentAmount: SignedDecimalLike,
    status: TaxAdjStatusSchema,
    postedBy: z.string().nullable().optional(),
    approvedBy: z.string().nullable().optional(),
    approvedAt: z.coerce.date().nullable().optional(),
    createdAt: z.coerce.date(),
    updatedAt: z.coerce.date(),
});
// ============================================================
// POST /tax-adjustments — create
// ============================================================
export const CreateTaxAdjustmentSchema = z.object({
    taxYear: z.coerce.number().int().min(2000).max(2100),
    code: TaxAdjCodeSchema,
    category: TaxAdjCategorySchema,
    description: z.string().min(1),
    section: z.string().nullable().optional(),
    bookAmount: z.coerce.number(),
    taxAmount: z.coerce.number(),
    // adjustmentAmount is computed server-side as (taxAmount - bookAmount)
    // BUT for add-backs the convention is "positive = increases taxable
    // income". The service handles the sign convention based on category.
});
// ============================================================
// PATCH /tax-adjustments/:id — edit
// ============================================================
export const UpdateTaxAdjustmentSchema = CreateTaxAdjustmentSchema.partial()
    .extend({
// taxYear is immutable post-create (would re-bucket the row into a
// different filing). Omit from partial via .omit on the result.
})
    .omit({ taxYear: true });
// ============================================================
// GET /tax-adjustments/summary — equation banner data
// ============================================================
export const TaxAdjSummarySchema = z.object({
    taxYear: z.number().int(),
    bookNetIncome: SignedDecimalLike,
    totalAddBack: SignedDecimalLike,
    totalDeduct: SignedDecimalLike,
    totalTiming: SignedDecimalLike,
    totalPermanent: SignedDecimalLike,
    // bookNetIncome + totalAddBack - totalDeduct (+ timing/permanent buckets
    // already absorbed into add-back/deduct via category). Always equals
    // taxableIncome regardless of TIMING/PERMANENT split.
    taxableIncome: SignedDecimalLike,
    // Statutory corporate income tax rate. Fixed at 0.20 (20%) for the
    // standard rate. SMEs and BOI-promoted entities have bracketed/reduced
    // rates — out of scope for Phase 3 (tracked as `taxRate` so the UI can
    // surface the effective rate when SME brackets are wired in Phase 4+).
    taxRate: z.number(),
    taxAmount: SignedDecimalLike,
    // Counts for KPI cards.
    countAddBack: z.number().int().nonnegative(),
    countDeduct: z.number().int().nonnegative(),
    countTotal: z.number().int().nonnegative(),
});
// List response shape — flat rows + summary.
export const TaxAdjustmentListResponseSchema = z.object({
    rows: z.array(TaxAdjustmentSchema),
    summary: TaxAdjSummarySchema,
});
//# sourceMappingURL=tax-adjustment.js.map